/**
 * Colour helpers and the widget's style objects.
 *
 * Everything the brand colour touches goes through here, for two reasons:
 *
 * 1. The colour comes from a client — the pasted snippet, or the console's
 *    free-text hex field via /chat/config. The old code appended two hex
 *    digits to it for alpha (`${brandColor}dd`), which only works for a
 *    6-digit hex: `#abc` became `#abcdd`, an invalid colour, so the gradient
 *    was dropped and the visitor's own bubbles rendered white-on-transparent
 *    (review S1). `normalizeBrandColor` settles the shape once and
 *    `withAlpha` builds an `rgba()` that cannot be malformed.
 *
 * 2. Contrast depends on the brand. White text on `#f59e0b` is 2.2:1; the
 *    brand as chip text on a white glass card is worse for a pale brand.
 *    `onBrandText` picks white or near-black for anything FILLED with the
 *    brand, and `brandInk` darkens a light brand until it reads as text on
 *    white (review S8). Both are computed once per brand in `buildStyles`.
 *
 * The style objects are inline React styles rather than a stylesheet because
 * the widget lands on host pages with arbitrary CSS and, often, a CSP with no
 * nonce for us — see README "Content Security Policy".
 */
import type { CSSProperties } from 'react';

export const DEFAULT_BRAND_COLOR = '#2563eb';

const HEX6 = /^#[0-9a-f]{6}$/i;
const HEX3 = /^#[0-9a-f]{3}$/i;

/**
 * `#rrggbb` (lower-case) for a 3- or 6-digit hex; the default for anything
 * else, with one console.warn so an installer can see why their colour did
 * not take. Empty/absent is "not set" and stays silent.
 */
export function normalizeBrandColor(input: unknown, warn = true): string {
  if (input === undefined || input === null || input === '') return DEFAULT_BRAND_COLOR;
  if (typeof input === 'string') {
    const t = input.trim();
    if (t === '') return DEFAULT_BRAND_COLOR;
    if (HEX6.test(t)) return t.toLowerCase();
    if (HEX3.test(t)) return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`.toLowerCase();
  }
  if (warn) {
    console.warn(
      `[PweChat] Ignoring brandColor ${JSON.stringify(input)} — expected a 6-digit hex colour like #2563eb. Using the default.`,
    );
  }
  return DEFAULT_BRAND_COLOR;
}

function channels(hex: string): [number, number, number] {
  const h = HEX6.test(hex) ? hex : DEFAULT_BRAND_COLOR;
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}

function toHex(r: number, g: number, b: number): string {
  const two = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${two(r)}${two(g)}${two(b)}`;
}

/** `rgba(r,g,b,a)` for a hex colour. Alpha is clamped to [0, 1]. */
export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = channels(hex);
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 1000) / 1000;
  return `rgba(${r},${g},${b},${a})`;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export const INK_LIGHT = '#ffffff';
export const INK_DARK = '#111111';

/** Text colour for something FILLED with the brand: whichever of white / near-black contrasts more. */
export function onBrandText(hex: string): string {
  return contrastRatio(INK_LIGHT, hex) >= contrastRatio(INK_DARK, hex) ? INK_LIGHT : INK_DARK;
}

/**
 * The brand as TEXT on a light background: darkened step by step until it
 * reaches AA (4.5:1) against `background`. A dark brand is returned as is, so
 * most clients see exactly their colour; only a pale one moves.
 */
export function brandInk(hex: string, background = INK_LIGHT, target = 4.5): string {
  let [r, g, b] = channels(hex);
  let out = toHex(r, g, b);
  for (let i = 0; i < 12 && contrastRatio(out, background) < target; i++) {
    r *= 0.85;
    g *= 0.85;
    b *= 0.85;
    out = toHex(r, g, b);
  }
  return out;
}

// ── Style objects ───────────────────────────────────────────────

export const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, sans-serif';

export const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * The honeypot field of the lead form. Off-screen and hidden from assistive
 * tech, but NOT display:none — a bot that skips invisible fields is exactly
 * the bot the honeypot exists to catch, and `display:none` is the first thing
 * they check.
 */
export const honeypotStyle: CSSProperties = {
  position: 'absolute',
  left: '-10000px',
  top: 'auto',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  opacity: 0,
};

export interface ThemeInput {
  /** Normalised `#rrggbb`. */
  brandColor: string;
  isLeft: boolean;
  isMobile: boolean;
  isExpanded: boolean;
  /** Visual viewport: width, height and its offset from the top of the layout viewport. */
  viewport: { w: number; h: number; top: number };
  sideOffset: number;
  bottomOffset: number;
}

export interface Styles {
  /** Text on a brand fill (user bubble, active send button). */
  ink: string;
  /** The brand as text/icon on the white glass (chips, launcher, admin label, links). */
  accent: string;
  chipBorder: string;
  chipHoverBackground: string;
  bubble: CSSProperties;
  bubbleGlow: CSSProperties;
  /** The invitation pill beside the closed launcher (desktop only). */
  launcherLabel: CSSProperties;
  panel: CSSProperties;
  header: CSSProperties;
  messages: CSSProperties;
  inputBar: CSSProperties;
  userBubble: CSSProperties;
  assistantBubble: CSSProperties;
  adminBubble: CSSProperties;
  input: CSSProperties;
  iconButton: CSSProperties;
  chip: CSSProperties;
  privacy: CSSProperties;
  formField: CSSProperties;
  formError: CSSProperties;
}

export function buildStyles(t: ThemeInput): Styles {
  const { brandColor, isLeft, isMobile, isExpanded, viewport, sideOffset, bottomOffset } = t;
  const ink = onBrandText(brandColor);
  const accent = brandInk(brandColor);

  // Horizontal anchor for the active corner (flips right → left for 'bottom-left').
  const horiz = (base: number): CSSProperties =>
    isLeft ? { left: `${base + sideOffset}px` } : { right: `${base + sideOffset}px` };
  // Vertical anchor from the bottom edge, plus any bottom offset.
  const vert = (base: number): string => `${base + bottomOffset}px`;

  const glassBase: CSSProperties = {
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  };

  const bubble: CSSProperties = {
    ...glassBase,
    position: 'fixed',
    bottom: vert(24),
    ...horiz(24),
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)',
    color: accent,
    border: '1px solid rgba(255,255,255,0.6)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxShadow:
      '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
    zIndex: 9999,
    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease',
  };

  // Sits on the bubble's inner side, vertically centred on it: the bubble is
  // 58px at 24px from the edge, the pill 36px tall, so 24 + 58 + 10 in and
  // 24 + 11 up. Same glass as the bubble, the brand as ink, one line only —
  // a long snippet label is cut with an ellipsis, never wrapped over the page.
  const launcherLabel: CSSProperties = {
    ...glassBase,
    position: 'fixed',
    bottom: vert(35),
    ...horiz(92),
    height: '36px',
    maxWidth: '240px',
    padding: '0 14px',
    borderRadius: '18px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
    color: accent,
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
    fontFamily: FONT_STACK,
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '36px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    zIndex: 9999,
  };

  const bubbleGlow: CSSProperties = {
    position: 'fixed',
    bottom: vert(14),
    ...horiz(14),
    width: '78px',
    height: '78px',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${withAlpha(brandColor, 0.25)} 0%, transparent 70%)`,
    filter: 'blur(12px)',
    pointerEvents: 'none',
    zIndex: 9998,
  };

  // On phones the floating card becomes a full-screen sheet — a 380px panel
  // hovering over a 390px viewport is what visitors notice most on mobile.
  // It is sized from the VISUAL viewport, not `innerHeight`: iOS Safari does
  // not shrink `innerHeight` for the keyboard, so a sheet measured from it
  // put the input bar under the keys (review S9). `top` follows the visual
  // viewport's offset for the same reason — when the keyboard scrolls the
  // layout viewport, the sheet scrolls with it.
  const mobileSheet: CSSProperties = isMobile
    ? {
        top: `${viewport.top}px`,
        bottom: 'auto',
        left: 0,
        right: 0,
        width: '100%',
        maxWidth: '100%',
        height: `${viewport.h}px`,
        maxHeight: 'none',
        borderRadius: 0,
      }
    : {};

  // The panel hangs from the bottom anchor — collapsed it clears the launcher
  // at 96px, expanded it sits at 24px — so its usable height is the viewport
  // MINUS that anchor, minus a matching gap at the top. A maxHeight measured
  // from the full viewport ignores the anchor and lets a short window (or the
  // console's Test Ema iframe) push the header off the top edge.
  const panelBottom = isExpanded ? 24 + bottomOffset : 96 + bottomOffset;
  const panelMaxHeight = `calc(100vh - ${panelBottom + 24}px)`;

  const panel: CSSProperties = {
    ...glassBase,
    position: 'fixed',
    bottom: `${panelBottom}px`,
    ...horiz(24),
    width: isExpanded ? '560px' : '380px',
    maxWidth: 'calc(100vw - 48px)',
    height: isExpanded ? panelMaxHeight : '520px',
    maxHeight: panelMaxHeight,
    borderRadius: '24px',
    // The 1px border must sit INSIDE the declared height, not add to it. The
    // widget lands on arbitrary host pages, so it cannot rely on the site
    // having a global border-box reset: without this, the mobile sheet's
    // `height: ${viewport.h}px` renders 2px taller than the viewport.
    boxSizing: 'border-box',
    transition:
      'width 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), bottom 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    background:
      'linear-gradient(160deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.80) 100%)',
    // The frame carries the brand, like everything else the client can set.
    // It was white-on-white: a client picked their colour, watched the
    // bubbles and the send button take it, and the window they all sat in
    // stayed the default. Kept translucent so a pale brand still reads as a
    // border and a dark one does not turn into a hard rule; the white inner
    // highlight below keeps the glass edge.
    border: `1px solid ${withAlpha(brandColor, 0.6)}`,
    boxShadow: `0 24px 80px ${withAlpha(brandColor, 0.16)}, 0 8px 24px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 9998,
    fontFamily: FONT_STACK,
    // Host pages set their own text defaults; pin the ones a chat panel
    // depends on so a theme's `line-height: 2` or centred text cannot leak in.
    fontSize: '14px',
    lineHeight: 1.5,
    textAlign: 'left',
    color: '#1a1a2e',
    animation: 'pweb-chat-slide-up 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    ...mobileSheet,
  };

  const header: CSSProperties = {
    padding: '18px 20px',
    // A WASH of the brand, not a fill of it. The header carries the business
    // name and its subtitle in near-black, and a client may pick any colour —
    // a solid brand header would have to work out its own text colour and
    // would still leave mid-tone brands unreadable. A low-alpha tint over the
    // white panel reads as the brand at any hue and never touches contrast.
    //
    // It is the SAME wash the Ema Answers console header wears (that app's
    // `components/Shell.tsx` HEADER_WASH): two ellipses hanging off the top
    // corners — a wide one at the left, a smaller, fainter one at the right —
    // over a top-to-bottom tint that is gone by two thirds of the way down.
    // The tint therefore FADES OUT into the panel instead of stopping on a
    // ruled edge.
    //
    // Sizes are PERCENTAGES of the header box, not the console's rem: this
    // bundle lands on arbitrary client pages, where `rem` is whatever the
    // host set on :root, and the panel itself is 380px or 560px wide
    // depending on `panelSize`. Alphas match the console's color-mix stops.
    background: [
      `radial-gradient(60% 450% at 0% -70%, ${withAlpha(brandColor, 0.22)}, transparent 70%)`,
      `radial-gradient(45% 350% at 100% -40%, ${withAlpha(brandColor, 0.12)}, transparent 65%)`,
      `linear-gradient(to bottom, ${withAlpha(brandColor, 0.06)}, transparent 65%)`,
    ].join(', '),
    // Neutral hairline, the console's `border-b border-border` — the same one
    // the input bar wears at the other end of the panel.
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexShrink: 0,
  };

  const messages: CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  };

  const inputBar: CSSProperties = {
    padding: '12px 16px',
    // The home indicator on a notched phone sits over the bottom 34px of the
    // full-screen sheet; pad the input bar by the safe area so the field and
    // the send button stay tappable (review S9).
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    borderTop: '1px solid rgba(0,0,0,0.06)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 100%)',
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  };

  const userBubble: CSSProperties = {
    ...glassBase,
    backdropFilter: 'blur(20px) saturate(150%)',
    WebkitBackdropFilter: 'blur(20px) saturate(150%)',
    alignSelf: 'flex-end',
    maxWidth: '85%',
    padding: '10px 16px',
    borderRadius: '20px 20px 6px 20px',
    background: `linear-gradient(135deg, ${withAlpha(brandColor, 0.87)} 0%, ${withAlpha(brandColor, 0.73)} 100%)`,
    color: ink,
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    boxShadow: `0 2px 12px ${withAlpha(brandColor, 0.2)}`,
  };

  const assistantBubble: CSSProperties = {
    ...glassBase,
    backdropFilter: 'blur(20px) saturate(150%)',
    WebkitBackdropFilter: 'blur(20px) saturate(150%)',
    alignSelf: 'flex-start',
    maxWidth: '85%',
    padding: '10px 16px',
    borderRadius: '20px 20px 20px 6px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 100%)',
    border: '1px solid rgba(0,0,0,0.06)',
    color: '#1a1a2e',
    fontSize: '14px',
    lineHeight: '1.5',
    wordBreak: 'break-word',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  };

  // Human operator replies: assistant-shaped, but visibly from a person.
  const adminBubble: CSSProperties = {
    ...assistantBubble,
    borderLeft: `3px solid ${brandColor}`,
  };

  const input: CSSProperties = {
    ...glassBase,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    flex: 1,
    minWidth: 0,
    padding: '10px 16px',
    borderRadius: '20px',
    border: '1px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.75)',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    color: '#1a1a2e',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  const iconButton: CSSProperties = {
    background: 'rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.08)',
    color: '#1a1a2e',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
  };

  const chipBorder = `1.5px solid ${withAlpha(brandColor, 0.27)}`;

  const chip: CSSProperties = {
    padding: '6px 14px',
    borderRadius: '18px',
    border: chipBorder,
    background: 'rgba(255,255,255,0.8)',
    color: accent,
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
    lineHeight: '1.3',
    textAlign: 'left',
  };

  const privacy: CSSProperties = {
    padding: '4px 16px 0',
    fontSize: '11px',
    // #9ca3af at 11px was ≈2.5:1 on the glass — below AA. #6b7280 clears it.
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: '1.4',
  };

  const formField: CSSProperties = {
    ...input,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: '12px',
    padding: '9px 12px',
  };

  const formError: CSSProperties = {
    fontSize: '12px',
    color: '#b91c1c',
    margin: '2px 0 0',
  };

  return {
    ink,
    accent,
    chipBorder,
    chipHoverBackground: withAlpha(brandColor, 0.07),
    bubble,
    bubbleGlow,
    launcherLabel,
    panel,
    header,
    messages,
    inputBar,
    userBubble,
    assistantBubble,
    adminBubble,
    input,
    iconButton,
    chip,
    privacy,
    formField,
    formError,
  };
}

/** The round send button; depends on per-keystroke state so it is not memoised with the rest. */
export function sendButtonStyle(brandColor: string, ink: string, active: boolean): CSSProperties {
  return {
    width: '40px',
    height: '40px',
    padding: 0,
    borderRadius: '50%',
    border: active ? 'none' : '1px solid rgba(0,0,0,0.08)',
    background: active
      ? `linear-gradient(135deg, ${withAlpha(brandColor, 0.87)}, ${brandColor})`
      : 'rgba(255,255,255,0.5)',
    color: active ? ink : '#6b7280',
    cursor: active ? 'pointer' : 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: active ? `0 4px 14px ${withAlpha(brandColor, 0.2)}` : 'none',
    transition: 'all 0.2s ease',
  };
}

/** Keyframes the panel and typing indicator use; injected once per page by the component. */
export const KEYFRAMES_STYLE_ID = 'pweb-chat-keyframes';
export const KEYFRAMES_CSS = `
@keyframes pweb-chat-slide-up {
  from { opacity: 0; transform: translateY(16px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes pweb-chat-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
`;
